import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { ShieldCheck, RotateCcw, Save, AlertTriangle } from 'lucide-react';

/**
 * Granular RBAC editor (Wave 33, audit-handoff §C.3).
 *
 * Renders the workspace permission matrix as one card per category.
 * Each row shows:
 *   - Permission label + description
 *   - Default minimum role (badge)
 *   - <Select> for the *effective* minimum role
 *   - "Reset" button when the effective role differs from the default
 *
 * The component is intentionally read-only for non-owners: the
 * backend rejects PUT /workspace/permissions for anyone below the
 * owner role, so we hide the save button for them rather than show
 * a 403 toast on click. Owners can override individual cells; only
 * cells that diverge from the default are persisted server-side
 * (see backend/permissions.normalise_overrides).
 */
export default function PermissionMatrix() {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [catalog, setCatalog] = useState({ roles: [], categories: [] });
  const [defaults, setDefaults] = useState({});
  const [effective, setEffective] = useState({});
  const [originalEffective, setOriginalEffective] = useState({});

  const dirty = useMemo(() => {
    const keys = new Set([...Object.keys(effective), ...Object.keys(originalEffective)]);
    for (const k of keys) {
      if (effective[k] !== originalEffective[k]) return true;
    }
    return false;
  }, [effective, originalEffective]);

  const overrides = useMemo(() => {
    const out = {};
    for (const [key, role] of Object.entries(effective)) {
      if (defaults[key] && role !== defaults[key]) {
        out[key] = role;
      }
    }
    return out;
  }, [effective, defaults]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [cat, mat] = await Promise.all([
          axios.get(`${API}/workspace/permissions/catalog`),
          axios.get(`${API}/workspace/permissions`),
        ]);
        if (cancelled) return;
        setCatalog(cat.data);
        setDefaults(mat.data.defaults || {});
        setEffective(mat.data.effective || {});
        setOriginalEffective(mat.data.effective || {});
        setCanEdit(Boolean(mat.data.can_edit));
      } catch (err) {
        if (!cancelled) {
          toast.error(t('Failed to load permission matrix'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  function setRole(key, role) {
    setEffective((prev) => ({ ...prev, [key]: role }));
  }

  function resetRow(key) {
    if (!defaults[key]) return;
    setEffective((prev) => ({ ...prev, [key]: defaults[key] }));
  }

  function resetAll() {
    setEffective({ ...defaults });
  }

  async function save() {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/workspace/permissions`, { overrides });
      setDefaults(res.data.defaults || defaults);
      setEffective(res.data.effective || effective);
      setOriginalEffective(res.data.effective || effective);
      toast.success(t('Permission matrix saved'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('Failed to save permission matrix'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          {t('Loading…')}
        </CardContent>
      </Card>
    );
  }

  const roles = catalog.roles || ['owner', 'admin', 'manager', 'viewer'];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            {t('Permission matrix')}
          </CardTitle>
          <CardDescription>
            {t(
              'Override the minimum role required for each action in this workspace. Only cells that differ from the default are persisted.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Badge variant="secondary">
            {Object.keys(overrides).length} {t('overrides')}
          </Badge>
          {!canEdit && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t('Read-only — only the workspace owner can edit the matrix.')}
            </span>
          )}
          {canEdit && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={resetAll}
                disabled={saving || Object.keys(overrides).length === 0}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('Reset all to defaults')}
              </Button>
              <Button onClick={save} disabled={saving || !dirty} size="sm">
                <Save className="w-4 h-4 mr-2" />
                {saving ? t('Saving…') : t('Save changes')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {(catalog.categories || []).map((cat) => (
        <Card key={cat.category}>
          <CardHeader>
            <CardTitle>{t(cat.category)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(cat.permissions || []).map((p) => {
              const current = effective[p.key] || p.default_min_role;
              const isOverride = current !== p.default_min_role;
              return (
                <div
                  key={p.key}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-2 border-b last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t(p.label)}</span>
                      {isOverride && <Badge variant="outline">{t('Custom')}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t(p.description)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('Default')}:{' '}
                      <span className="font-mono">{t(p.default_min_role)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select
                      value={current}
                      onValueChange={(v) => setRole(p.key, v)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r} value={r}>
                            {t(r)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {canEdit && isOverride && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => resetRow(p.key)}
                        aria-label={t('Reset to default')}
                        title={t('Reset to default')}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
